import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, PencilSimple, Trash, X, ArrowCounterClockwise,
  CaretDown, CaretRight,
} from "@phosphor-icons/react";

// Match the three canonical capabilities the backend exposes for every module
const CAPS = [
  { key: "view", label: "View only", color: "#0B2B66" },
  { key: "edit", label: "Can edit",  color: "#7A5A11" },
  { key: "add",  label: "Can add",   color: "#2D5A27" },
];

export default function RolesPermissions() {
  const [matrix, setMatrix] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);
  const [editing, setEditing] = useState(null); // {mode, roleKey?, custom?}

  const loadAll = async () => {
    try {
      const [m, c, r] = await Promise.all([
        api.get("/roles/matrix"),
        api.get("/modules-catalog"),
        api.get("/custom-roles"),
      ]);
      setMatrix(m.data);
      setCatalog(c.data);
      setCustomRoles(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  const resetBuiltIn = async (roleKey) => {
    if (!window.confirm(`Revert "${roleKey}" back to the shipped default permissions?`)) return;
    try {
      await api.delete(`/roles/matrix/${roleKey}`);
      toast.success(`${roleKey} reset to default`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeCustom = async (role) => {
    if (!window.confirm(`Delete role "${role.label}"?`)) return;
    try {
      await api.delete(`/custom-roles/${role.id}`);
      toast.success("Role deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!matrix || !catalog) {
    return <AppShell title="Roles & Permissions"><div className="text-sm text-[#8A8780]">Loading…</div></AppShell>;
  }

  return (
    <AppShell title="Roles & Permissions" subtitle="Staff Management"
      actions={
        <button data-testid="btn-new-role"
                onClick={() => setEditing({ mode: "new-custom" })}
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
          <Plus size={14} weight="bold"/>New custom role
        </button>
      }>

      {/* Built-in roles — editable */}
      <div className="bg-white border border-[#E5E2D9] rounded-md mb-6">
        <div className="p-5 border-b border-[#E5E2D9]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
            Built-in roles · click to edit
          </div>
          <div className="font-heading text-base mt-1">
            System roles ({Object.keys(matrix.roles).length})
          </div>
          <p className="text-xs text-[#5C5A55] mt-1">
            Edit any role to grant module-level access. Reset any role back to shipped defaults at any time.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-5" data-testid="builtin-roles-matrix">
          {Object.entries(matrix.roles).map(([role, perms]) => {
            const overridden = Boolean(matrix.overrides?.[role]);
            return (
              <div key={role} className="border border-[#E5E2D9] rounded-md p-4 hover:border-[#2D5A27] transition">
                <div className="flex items-start gap-2 mb-2">
                  <ShieldCheck size={16} color="#2D5A27" weight="duotone" className="mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <div className="text-sm font-medium capitalize">{role.replace(/_/g, " ")}</div>
                      {(matrix.tracked_roles || []).includes(role) && (
                        <span className="text-[9px] uppercase tracking-[0.18em] bg-[#F9E4B7] text-[#7A5A11] px-2 py-0.5 rounded">tracked</span>
                      )}
                      {overridden && (
                        <span className="text-[9px] uppercase tracking-[0.18em] bg-[#F4C7B9] text-[#7A2E18] px-2 py-0.5 rounded">customised</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {perms.slice(0, 8).map(p => (
                        <span key={p} className="text-[10px] font-mono bg-[#F7F6F2] text-[#5C5A55] px-1.5 py-0.5 rounded">{p}</span>
                      ))}
                      {perms.length > 8 && (
                        <span className="text-[10px] text-[#8A8780]">+{perms.length - 8}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => setEditing({ mode: "edit-builtin", roleKey: role,
                                                       currentPerms: perms })}
                          data-testid={`edit-builtin-${role}`}
                          className="text-xs inline-flex items-center gap-1 h-8 px-3 border border-[#E5E2D9] rounded-md hover:bg-[#F7F6F2] text-[#2D5A27]">
                    <PencilSimple size={12}/>Edit
                  </button>
                  {overridden && (
                    <button onClick={() => resetBuiltIn(role)}
                            data-testid={`reset-builtin-${role}`}
                            className="text-xs inline-flex items-center gap-1 h-8 px-3 border border-[#E5E2D9] rounded-md hover:bg-[#F7F6F2] text-[#5C5A55]">
                      <ArrowCounterClockwise size={12}/>Reset to default
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom roles */}
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="p-5 border-b border-[#E5E2D9]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Custom roles</div>
          <div className="font-heading text-base mt-1">
            {customRoles.length} tenant-specific role{customRoles.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="divide-y divide-[#E5E2D9]" data-testid="custom-roles-list">
          {customRoles.length === 0 && (
            <div className="p-8 text-center text-sm text-[#8A8780]">
              No custom roles yet. Click <b>New custom role</b> to define one.
            </div>
          )}
          {customRoles.map(r => (
            <div key={r.id} className="p-5 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{r.label}</span>
                  <code className="text-[11px] bg-[#F7F6F2] text-[#5C5A55] px-1.5 py-0.5 rounded">{r.key}</code>
                  {r.user_count > 0 && (
                    <span className="text-[10px] bg-[#D4E0D1] text-[#1A3317] px-2 py-0.5 rounded">
                      {r.user_count} user{r.user_count === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                {r.description && <p className="text-xs text-[#5C5A55] mt-1">{r.description}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {(r.permissions || []).slice(0, 12).map(p => (
                    <span key={p} className="text-[10px] font-mono bg-[#EFF3EE] text-[#1A3317] px-2 py-0.5 rounded">{p}</span>
                  ))}
                  {r.permissions?.length > 12 && (
                    <span className="text-[10px] text-[#8A8780]">+{r.permissions.length - 12}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setEditing({ mode: "edit-custom", custom: r })}
                        data-testid={`edit-role-${r.id}`}
                        className="text-[#2D5A27] text-xs inline-flex items-center gap-1 h-8 px-3 border border-[#E5E2D9] rounded-md hover:bg-[#F7F6F2]">
                  <PencilSimple size={12}/>Edit
                </button>
                <button onClick={() => removeCustom(r)}
                        data-testid={`delete-role-${r.id}`}
                        disabled={r.user_count > 0}
                        title={r.user_count > 0 ? `Reassign the ${r.user_count} user(s) first` : "Delete role"}
                        className="text-[#C25D38] text-xs inline-flex items-center gap-1 h-8 px-3 border border-[#E5E2D9] rounded-md hover:bg-[#FDE9E4] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash size={12}/>Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <RoleEditor
          catalog={catalog}
          mode={editing.mode}
          builtInRoleKey={editing.roleKey}
          initialPerms={editing.currentPerms}
          customRole={editing.custom}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}
    </AppShell>
  );
}


// ─────────────────────────────────────────────────────────────
// Role editor — supports: new custom · edit custom · edit built-in
// ─────────────────────────────────────────────────────────────
function RoleEditor({ catalog, mode, builtInRoleKey, initialPerms,
                       customRole, onClose, onSaved }) {
  const isBuiltIn = mode === "edit-builtin";
  const isNewCustom = mode === "new-custom";
  const isEditCustom = mode === "edit-custom";

  const [form, setForm] = useState({
    key: customRole?.key || "",
    label: customRole?.label || (isBuiltIn ? builtInRoleKey.replace(/_/g, " ") : ""),
    description: customRole?.description || "",
    permissions: new Set(customRole?.permissions || initialPerms || []),
  });
  const [busy, setBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(() => new Set());
  const [subOpen, setSubOpen] = useState(() => new Set());

  const has = (k) => {
    if (form.permissions.has(k)) return true;
    // Wildcard match — e.g. "customers.*" grants "customers.view".
    // For a 3-part submodule key ("crm.leads.view"), match "crm.leads.*" AND "crm.*".
    const parts = k.split(".");
    for (let i = parts.length - 1; i >= 1; i--) {
      const prefix = parts.slice(0, i).join(".");
      if (form.permissions.has(`${prefix}.*`)) return true;
    }
    if (form.permissions.has("*")) return true;
    return false;
  };
  const isWildcard = (moduleKey) => form.permissions.has(`${moduleKey}.*`) || form.permissions.has("*");

  const toggle = (key) => {
    const next = new Set(form.permissions);
    if (next.has(key)) next.delete(key); else next.add(key);
    setForm(f => ({ ...f, permissions: next }));
  };

  const setCap = (moduleKey, capKey, on) => {
    // moduleKey can be either a parent (e.g. "crm") or a submodule (e.g. "crm.leads").
    const key = `${moduleKey}.${capKey}`;
    const next = new Set(form.permissions);
    if (on) {
      next.add(key);
      // Enabling ADD implies EDIT and VIEW; enabling EDIT implies VIEW.
      if (capKey === "add") { next.add(`${moduleKey}.edit`); next.add(`${moduleKey}.view`); }
      if (capKey === "edit") { next.add(`${moduleKey}.view`); }
    } else {
      next.delete(key);
      // Disabling VIEW removes EDIT + ADD; disabling EDIT removes ADD
      if (capKey === "view") { next.delete(`${moduleKey}.edit`); next.delete(`${moduleKey}.add`); }
      if (capKey === "edit") { next.delete(`${moduleKey}.add`); }
    }
    // Any explicit per-capability edit clears that scope's wildcard
    next.delete(`${moduleKey}.*`);
    setForm(f => ({ ...f, permissions: next }));
  };

  const toggleWildcard = (moduleKey) => {
    const key = `${moduleKey}.*`;
    const next = new Set(form.permissions);
    if (isWildcard(moduleKey)) {
      next.delete(key);
    } else {
      // Add wildcard, remove any explicit children of this module for cleanliness
      next.add(key);
      for (const k of Array.from(next)) {
        if (k.startsWith(`${moduleKey}.`) && k !== key) next.delete(k);
      }
    }
    setForm(f => ({ ...f, permissions: next }));
  };

  const toggleAdv = (mk) => {
    const next = new Set(advancedOpen);
    if (next.has(mk)) next.delete(mk); else next.add(mk);
    setAdvancedOpen(next);
  };

  const toggleSub = (mk) => {
    const next = new Set(subOpen);
    if (next.has(mk)) next.delete(mk); else next.add(mk);
    setSubOpen(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    const perms = Array.from(form.permissions);
    setBusy(true);
    try {
      if (isBuiltIn) {
        await api.put(`/roles/matrix/${builtInRoleKey}`, { permissions: perms });
      } else if (isEditCustom) {
        await api.put(`/custom-roles/${customRole.id}`, {
          label: form.label.trim(),
          description: form.description.trim(),
          permissions: perms,
        });
      } else {
        // new custom
        if (!/^[a-z0-9_]{2,40}$/.test(form.key.toLowerCase().trim())) {
          toast.error("Key must be 2–40 chars, lowercase letters/digits/underscore only");
          setBusy(false); return;
        }
        if (!form.label.trim()) { toast.error("Label required"); setBusy(false); return; }
        await api.post("/custom-roles", {
          key: form.key.toLowerCase().trim(),
          label: form.label.trim(),
          description: form.description.trim(),
          permissions: perms,
        });
      }
      toast.success(
        isBuiltIn ? `${builtInRoleKey} permissions updated`
        : isEditCustom ? "Role updated" : "Role created",
      );
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const title = isBuiltIn
    ? `Edit built-in role: ${builtInRoleKey}`
    : isEditCustom ? `Edit custom role: ${customRole.label}` : "New custom role";
  const totalPicked = form.permissions.size;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            data-testid="role-editor-modal"
            className="bg-white rounded-md w-full max-w-4xl my-8 max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              {isBuiltIn ? "Built-in role · editable" : "Custom role"}
            </div>
            <div className="font-heading text-lg mt-1">{title}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>

        <div className="p-6 space-y-5">
          {!isBuiltIn && (
            <>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-[#5C5A55] mb-1">Key {isEditCustom && "(locked)"}</label>
                  <input value={form.key} disabled={isEditCustom}
                         onChange={e => setForm(f => ({ ...f, key: e.target.value }))}
                         data-testid="role-editor-key"
                         placeholder="e.g. logistics_lead"
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm font-mono disabled:bg-[#F7F6F2] disabled:text-[#8A8780] focus:border-[#2D5A27] outline-none"/>
                </div>
                <div>
                  <label className="block text-[11px] text-[#5C5A55] mb-1">Label</label>
                  <input value={form.label}
                         onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                         data-testid="role-editor-label"
                         placeholder="e.g. Logistics Lead"
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#2D5A27] outline-none"/>
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-[#5C5A55] mb-1">Description</label>
                <input value={form.description}
                       onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                       data-testid="role-editor-desc"
                       placeholder="What this role is for"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#2D5A27] outline-none"/>
              </div>
            </>
          )}

          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                Control modules · per-capability
              </label>
              <span className="text-[11px] text-[#5C5A55]">{totalPicked} selected</span>
            </div>

            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] grid grid-cols-[1fr_repeat(4,minmax(80px,auto))] gap-2 px-3 pb-2">
              <div>Module</div>
              {CAPS.map(c => <div key={c.key} className="text-center">{c.label}</div>)}
              <div className="text-center">All (*)</div>
            </div>

            <div className="border border-[#E5E2D9] rounded-md divide-y divide-[#E5E2D9]"
                 data-testid="role-editor-modules">
              {catalog.modules.map(m => {
                const advanced = m.children.filter(c => c.capability === "advanced");
                const advOn = advanced.filter(a => form.permissions.has(a.key)).length;
                const wildcardOn = isWildcard(m.key);
                return (
                  <div key={m.key}>
                    <div className={`grid grid-cols-[1fr_repeat(4,minmax(80px,auto))] gap-2 items-center px-3 py-2.5 ${wildcardOn ? "bg-[#EFF3EE]" : ""}`}>
                      <div>
                        <div className="text-sm font-medium text-slate-900">{m.label}</div>
                        <code className="text-[10px] text-[#8A8780]">{m.key}</code>
                      </div>
                      {CAPS.map(cap => {
                        const on = wildcardOn || has(`${m.key}.${cap.key}`);
                        return (
                          <div key={cap.key} className="flex justify-center">
                            <input type="checkbox"
                                   checked={on}
                                   disabled={wildcardOn}
                                   onChange={e => setCap(m.key, cap.key, e.target.checked)}
                                   data-testid={`cap-${m.key}-${cap.key}`}
                                   className="accent-[#2D5A27] w-4 h-4 cursor-pointer disabled:opacity-60"/>
                          </div>
                        );
                      })}
                      <div className="flex justify-center">
                        <input type="checkbox"
                               checked={wildcardOn}
                               onChange={() => toggleWildcard(m.key)}
                               data-testid={`cap-${m.key}-all`}
                               title="Grant full access to this module (module.*)"
                               className="accent-[#0B2B66] w-4 h-4 cursor-pointer"/>
                      </div>
                    </div>
                    {advanced.length > 0 && (
                      <div className="px-3 pb-2">
                        <button type="button" onClick={() => toggleAdv(m.key)}
                                className="inline-flex items-center gap-1 text-[10px] text-[#8A8780] hover:text-[#5C5A55]">
                          {advancedOpen.has(m.key) ? <CaretDown size={10}/> : <CaretRight size={10}/>}
                          Advanced ({advOn}/{advanced.length})
                        </button>
                        {advancedOpen.has(m.key) && (
                          <div className="flex flex-wrap gap-3 mt-2 pl-3">
                            {advanced.map(a => (
                              <label key={a.key} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="checkbox"
                                       checked={has(a.key)}
                                       disabled={wildcardOn}
                                       onChange={() => toggle(a.key)}
                                       data-testid={`adv-${a.key}`}
                                       className="accent-[#2D5A27] disabled:opacity-60"/>
                                <span className="text-[#5C5A55]">{a.label}</span>
                                <code className="text-[10px] text-[#B0AEA5]">{a.key}</code>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {(m.submodules || []).length > 0 && (
                      <div className="px-3 pb-2 border-t border-[#F2F0E9] mt-1 pt-2">
                        <button type="button" onClick={() => toggleSub(m.key)}
                                data-testid={`sub-toggle-${m.key}`}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#5C5A55] hover:text-slate-900">
                          {subOpen.has(m.key) ? <CaretDown size={10}/> : <CaretRight size={10}/>}
                          Submodules ({m.submodules.length})
                        </button>
                        {subOpen.has(m.key) && (
                          <div className="mt-2 rounded-md border border-[#F2F0E9] divide-y divide-[#F2F0E9] bg-[#FBFAF6]">
                            {m.submodules.map(sm => {
                              const smWildOn = form.permissions.has(`${sm.key}.*`) || wildcardOn;
                              return (
                                <div key={sm.key}
                                     className="grid grid-cols-[1fr_repeat(4,minmax(80px,auto))] gap-2 items-center px-4 py-2"
                                     data-testid={`submod-${sm.key}`}>
                                  <div className="pl-4 flex items-center gap-2">
                                    <span className="text-[#B0AEA5]">↳</span>
                                    <div>
                                      <div className="text-[13px] text-slate-800">{sm.label}</div>
                                      <code className="text-[10px] text-[#8A8780]">{sm.key}</code>
                                    </div>
                                  </div>
                                  {CAPS.map(cap => {
                                    const on = smWildOn || has(`${sm.key}.${cap.key}`);
                                    return (
                                      <div key={cap.key} className="flex justify-center">
                                        <input type="checkbox"
                                               checked={on}
                                               disabled={wildcardOn}
                                               onChange={e => setCap(sm.key, cap.key, e.target.checked)}
                                               data-testid={`sub-cap-${sm.key}-${cap.key}`}
                                               className="accent-[#2D5A27] w-4 h-4 cursor-pointer disabled:opacity-60"/>
                                      </div>
                                    );
                                  })}
                                  <div className="flex justify-center">
                                    <input type="checkbox"
                                           checked={smWildOn}
                                           disabled={wildcardOn}
                                           onChange={() => {
                                             const wk = `${sm.key}.*`;
                                             const next = new Set(form.permissions);
                                             if (next.has(wk)) { next.delete(wk); }
                                             else {
                                               next.add(wk);
                                               // clean explicit children
                                               for (const k of Array.from(next)) {
                                                 if (k.startsWith(`${sm.key}.`) && k !== wk) next.delete(k);
                                               }
                                             }
                                             setForm(f => ({ ...f, permissions: next }));
                                           }}
                                           data-testid={`sub-cap-${sm.key}-all`}
                                           title="Grant full access to this submodule"
                                           className="accent-[#0B2B66] w-4 h-4 cursor-pointer disabled:opacity-60"/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isBuiltIn && (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#5C5A55]">
                <span>Or grant everything:</span>
                <button type="button"
                        onClick={() => {
                          const next = new Set(form.permissions);
                          if (next.has("*")) next.delete("*");
                          else next.add("*");
                          setForm(f => ({ ...f, permissions: next }));
                        }}
                        data-testid="cap-star"
                        className={`inline-flex items-center gap-1 h-7 px-3 rounded-md border text-[11px] ${form.permissions.has("*") ? "bg-[#0B2B66] text-white border-[#0B2B66]" : "border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
                  <ShieldCheck size={11}/>Full super-admin (*)
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid="role-editor-save"
                  className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : isBuiltIn ? "Save permissions" : isEditCustom ? "Save changes" : "Create role"}
          </button>
        </div>
      </form>
    </div>
  );
}
